import { computed } from 'vue';
import { useState } from 'nuxt/app';
import { useRoute, useRouter } from 'vue-router';
import { routes } from "~/constants/routes";
import { useFetchWithJwt } from '~/composables/useFetchWithJwt';
import {useHandleCode} from "~/composables/useHandleCode";
import {usePagination} from "~/composables/usePagination";
import type {
    ApiResponse,
    ApiResponseWithPage,
    PageData
} from '~/types/response'
import type {
    TermsData,
    TermsDataDisplay,
    CreateTermsRequest,
    UpdateTermsRequest,
    TermsDetailResponse,
    TermsResultResponse
} from '~/types/terms'
import {TermsAggrementRequest} from "~/types/terms";

export function useTermsApi() {
    const { fetchWithJwt } = useFetchWithJwt();
    const { handleErrorCode, handleSuccessCode } = useHandleCode();

    const customPaginationKey = window.location.pathname;//페이지 키 값을 경로로 지정
	const {setParams, setFetchFunction, updatePagination} = usePagination(customPaginationKey);

    const route = useRoute();
    const router = useRouter();

    const { lang } = useLanguage();

    // 페이지 id 가져오기
    const termsRouteId = route.query.id ? Number(route.query.id) : null;

    const termsArrayList = useState<TermsDataDisplay[]>('termsArrayList', () => []);

    const termsId = useState<number>('termsId', () => 0);
    const termsType = useState<string | null>('termsType', () => '');
    const termsContent = useState<string>('termsContent', () => '');
    const termsContentHtml = useState<string>('termsContentHtml', () => '');
    const termsContentEn = useState<string>('termsContentEn', () => '');
    const termsContentZh = useState<string>('termsContentZh', () => '');
    const termsStartDate = useState<string>('termsStartDate', () => '');
    const termsUpdateDateTime = useState<string>('termsUpdateDateTime', () => '');
    const termsPeriod = useState<number | null>('termsPeriod', () => null);
    const termsIsActive = useState<number | null>('termsIsActive', () => null);

    // 약관 등록용
    // const termsId = useState<number>('termsId', () => 0);
    const termsRegType = useState<string | null>('termsRegType', () => null);
    const termsRegContent = useState<string>('termsRegContent', () => '');
    const termsRegContentEn = useState<string>('termsRegContentEn', () => '');
    const termsRegContentZh = useState<string>('termsRegContentZh', () => '');
    const termsRegIsActive = useState<number | null>('termsRegIsActive', () => null);
    const termsRegStartDate = useState<string>('termsRegStartDate', () => '');
    const termsRegPeriod = useState<number | null>('termsRegPeriod', () => null);

    const selectedTermsType = useState<string | null>('selectedTermsType', () => 'PRIVACY');
    const selectedActive = useState<number | null>('selectedActive', () => null);

    // 팝업용 약관 정보
    const popupTerms = useState<TermsData | null>('popupTerms', () => null);
    // 팝업용 약관 내용
    const popupTermsContent = useState<string>('popupTermsContent', () => '');
    const popupTermsContentEn = useState<string>('popupTermsContentEn', () => '');
    const popupTermsContentZh = useState<string>('popupTermsContentZh', () => '');
    // 팝업용 약관 html
    const popupTermsContentHtml = useState<string>('popupTermsContentHtml', () => '');
    // 팝업용 약관 리스트
    const popupTermsList = useState<TermsDataDisplay[]>('popupTermsList', () => []);

    const fetchTermsDetail = async (id: number): Promise<ApiResponse<TermsDetailResponse>> => {
        return (await fetchWithJwt(`/terms/detail/${id}`, {
            method: 'GET',
        })) as ApiResponse<TermsDetailResponse>;
    };

    const fetchTermsList = async (params: Record<string, any>): Promise<ApiResponseWithPage<TermsData>> => {
        return (await fetchWithJwt('/terms/search', {
            method: 'POST',
            body: JSON.stringify({
                termsType: selectedTermsType.value,
                isActive: selectedActive.value,
                page: params.page,
                size: params.size,
                ...params,
            }),
        })) as ApiResponseWithPage<TermsData>
    };

    const fetchCreateTermsItem = async (body: CreateTermsRequest): Promise<ApiResponse<TermsResultResponse>> => {
        return await fetchWithJwt('/terms/create', {
            method: 'PUT',
            body: JSON.stringify(body),
        }) as ApiResponse<TermsResultResponse>;
    };

    const fetchUpdateTermsItem = async (body: UpdateTermsRequest): Promise<ApiResponse<TermsResultResponse>> => {
        return await fetchWithJwt('/terms/update', {
            method: 'POST',
            body: JSON.stringify(body),
        }) as ApiResponse<TermsResultResponse>;
    };

    const fetchDeleteTermsItem = async (id: number): Promise<ApiResponse<TermsResultResponse>> => {
        return await fetchWithJwt(`/terms/delete/${id}`, {
            method: 'DELETE',
        }) as ApiResponse<TermsResultResponse>;
    };

    // 최신 약관 조회
    const fetchLatestTerms = async (type: string): Promise<ApiResponse<TermsData>> => {
        return await fetchWithJwt('/terms/' + type +'/latest', {
            method: 'GET',
        }) as ApiResponse<TermsData>;
    };

    // 사용자가 동의한 약관 목록 조회
    const fetchUserAgreedTerms = async (): Promise<ApiResponse<TermsData[]>> => {
        return await fetchWithJwt(`/terms/user`, {
            method: 'GET',
        }) as ApiResponse<TermsData[]>;
    };

    // 약관 리스트 조회
    const handlerTermsList = async (params: Record<string, any>): Promise<PageData<TermsData>> => {
        console.log('[handlerTermsList] 시작');
        try {
            const response = await fetchTermsList(params);

            if (response?.result === 'success') {
                // Number로 변환
                const page = Number(params.page ?? 0);
                const size = Number(params.size ?? 10);

                termsArrayList.value = response.data?.content.map((item:TermsData, index) => {
                    let activeText = ''
                    let termsTypeText = ''

                    if (item.isActive === 0) {
                        activeText = lang.constant('USE_STATUS.0')
                    } else if (item.isActive === 1) {
                        activeText = lang.constant('USE_STATUS.1')
                    }

                    if (item.termsType === 'OF_USE') {
                        termsTypeText = lang.constant('TERMS_TYPE.OF_USE')
                    } else if (item.termsType === 'PRIVACY') {
                        termsTypeText = lang.constant('TERMS_TYPE.PRIVACY')
                    } else if (item.termsType === 'PROVISION') {
                        termsTypeText = lang.constant('TERMS_TYPE.PROVISION')
                    }

                    return {
                        ...item,
                        index: (page * size) + index + 1,
                        termsType: termsTypeText,
                        isActive: activeText,
                        termsPeriod: item.termsPeriod + lang.label('year'),
                        createdDateTime: item.createdDateTime.replace("T", " "),
                    }
                }) as TermsDataDisplay[];

                await updatePagination(response.data as PageData<TermsData>);
                setParams(params);
                setFetchFunction(handlerTermsList); // 중요

                return response.data as PageData<TermsData>;
            } else {
                handleErrorCode(response.message);
                return { content: [], totalPages: 0, totalElements: 0, number: 0, size: 0, first: false, last: false };
            }
        } catch (error) {
            console.error('Error fetching beta data:', error);
            handleErrorCode('E50001');
            return { content: [], totalPages: 0, totalElements: 0, number: 0, size: 0, first: false, last: false };
        }
    }

    // 약관 상세 조회
    const handlerTermsDetail = async (termsRouteId: any) => {
        console.log('[handlerTermsDetail] 시작');
        try {
            const response = await fetchTermsDetail(termsRouteId);

            if (response.result === 'success') {
                termsId.value = response.data.id;
                termsType.value = response.data.termsType;
                termsContent.value = response.data.termsContent;
                termsContentEn.value = response.data.data?.termsContentEn;
                termsContentZh.value = response.data.data?.termsContentZh;
                termsContentHtml.value = response.data.termsContentHtml;
                termsStartDate.value = response.data.startDate;
                termsUpdateDateTime.value = response.data.updateDateTime.split("T")[0];
                termsPeriod.value = response.data.termsPeriod;
                termsIsActive.value = response.data.isActive;

            } else {
                handleErrorCode(response.message);
				console.warn("❌ 약관 데이터를 찾을 수 없습니다.");
            }
        } catch (error) {
            handleErrorCode('E50000');
			console.error("❌ 약관 불러오기 실패:", error);
        }
        console.log('[handlerTermsDetail] 종료');
    }

    // 약관 수정
    const handlerTermsUpdate = async () => {
        console.log('[handlerTermsUpdate] 시작');
        const payload = {
            termsType: termsType.value,
            termsContent: termsContent.value,
            termsContentHtml: `<p>${termsContent.value}</p>`,
            isAcitve: termsIsActive.value,
            startDate: termsStartDate.value,
            termsPeriod: termsPeriod.value,
            data: {
                TERMS: getTermsLabels(termsType.value),
                termsContentEn: termsContentEn.value,
                termsContentHtmlEn: `<p>${termsContentEn.value}</p>`,
                termsContentZh: termsContentZh.value,
                termsContentHtmlZh: `<p>${termsContentZh.value}</p>`
            }
        }

        try {
            const response = await fetchUpdateTermsItem({ id: termsId.value, ...payload });
            if (response && response.result === "success") {
                handleSuccessCode('S00003');
                router.push({path: '/management/terms/terms_page'})
            } else if (response && response.result === "fail") {
				// 에러 코드 처리
				handleErrorCode(response.message);
			}
        } catch (error) {
			handleErrorCode('E50000');
			console.error('현장 수정 실패:', error);
		}
        console.log('[handlerTermsUpdate] 종료');
    }

    // 최신 약관 조회
    const handlerFetchLatestTerms = async (type: string): Promise<void> => {
        console.log('[handlerFetchLatestTerms] 시작');
        try {
            const response = await fetchLatestTerms(type);
            if (response.result === 'success' && response.data) {
                popupTerms.value = response.data;
                popupTermsContent.value = response.data.termsContent
                popupTermsContentEn.value = response.data.data?.termsContentEn;
                popupTermsContentZh.value = response.data.data?.termsContentZh;
                popupTermsContentHtml.value = response.data.termsContentHtml;
            } else if(response && response.result === 'fail') {
                handleErrorCode(response.message);
            }
        } catch (error) {
            console.error('최신 약관 조회 실패:', error);
            handleErrorCode('E50000');
        }
        console.log('[handlerFetchLatestTerms] 종료');
    }

    // 사용자가 동의한 약관 목록 조회
    const handlerFetchUserAgreedTerms = async (): Promise<void> => {
        console.log('[handlerFetchUserAgreedTerms] 시작');
        try {
            const response = await fetchUserAgreedTerms();
            if (response.result === 'success' && response.data) {
                // id 기준 내림차순 정렬
                const sorted = [...response.data].sort((a, b) => Number(b.id) - Number(a.id));

                // 팝업에 표시할 약관 정보
                popupTerms.value = sorted[0];
                // 팝업에 표시할 약관 정보 내용
                popupTermsContent.value = sorted[0].termsContent;
                // 팝업에 표시할 약관 정보 내용
                popupTermsContentHtml.value = sorted[0].termsContentHtml;

                // 각 언어별 팝업에 표시할 약관 리스트
                popupTermsList.value = sorted.map((item: TermsData, index): TermsDataDisplay => {
                    const activeText = item.isActive === 0
                        ? lang.constant('USE_STATUS.0')
                        : lang.constant('USE_STATUS.1');

                    return {
                        ...item, // 나머지 필드는 동일하므로 간단히 전개
                        index: index + 1,
                        isActive: activeText,
                        isDeleted: item.isActive === 0 ? lang.constant('USE_STATUS.0') : lang.constant('USE_STATUS.1'),
                        termsPeriod: item.termsPeriod ? `${item.termsPeriod}${lang.label('year')}` : '',
                        createdDateTime: item.createdDateTime.replace("T", " "),
                        updateDateTime: item.updateDateTime.replace("T", " "),
                        termsContentEn: item.data?.termsContentEn || '',
                        termsContentZh: item.data?.termsContentZh || '',
                    };
                });
            } else if(response && response.result === 'fail') {
                handleErrorCode(response.message);
            }
        } catch (error) {
            console.error('사용자가 동의한 약관 목록 조회 실패:', error);
            handleErrorCode('E50000');
        }
        console.log('[handlerFetchUserAgreedTerms] 종료');
    }

    // YYYY-MM-DD 형식으로 포맷하는 함수
    const formatDate = (date: string | Date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // data의 값
    const getTermsLabels = (type: string | null) => {
        switch (type) {
            case 'OF_USE':
                return lang.constant('TERMS_TYPE.OF_USE')
            case 'PRIVACY':
                return lang.constant('TERMS_TYPE.PRIVACY')
            case 'PROVISION':
                return lang.constant('TERMS_TYPE.PROVISION')
            default:
                return '';
        }
    }

    // 약관 등록
    // skytree
    const handlerTermsCreate = async () => {
        console.log('[handlerTermsCreate] 시작');
        const payload = {
            termsType: termsRegType.value,
            termsContent: termsRegContent.value,
            termsContentHtml: `<p>${termsRegContent.value}</p>`,
            isActive: termsRegIsActive.value,
            startDate: formatDate(termsRegStartDate.value),
            termsPeriod: termsRegPeriod.value,
            data: {
                TERMS: getTermsLabels(termsRegType.value),
                termsContentEn: termsRegContentEn.value,
                termsContentHtmlEn: `<p>${termsRegContentEn.value}</p>`,
                termsContentZh: termsRegContentZh.value,
                termsContentHtmlZh: `<p>${termsRegContentZh.value}</p>`
            }
        }

        try {
            const response = await fetchCreateTermsItem(payload);
		    if (response && response.result === "success") {
                handleSuccessCode('S00002');
                router.push('/management/terms/terms_page');
            } else if (response && response.result === "fail") {
				// 에러 코드 처리
				handleErrorCode(response.message);
			}
        } catch (error) {
            console.error('약관 등록 실패:', error);
        }
        console.log('[handlerTermsCreate] 종료');
    }

    // 약관 삭제
    const handlerTermsDelete = async (termsRouteId: any) => {
        console.log('[handlerTermsDelete] 시작');
        try {
            const response = await fetchDeleteTermsItem(termsRouteId);
            if (response && response.result === 'success') {
                handleSuccessCode('S00004');
				// 페이지 이동
                router.push({path: '/management/terms/terms_page'})
            } else if (response && response.result === "fail") {
				// 에러 코드 처리
				handleErrorCode(response.message);
			}
        } catch (error) {
			handleErrorCode('E50000');
			console.error("❌ 삭제 중 오류 발생:", error);
		}
        console.log('[handlerTermsDelete] 종료');
    }

    // 테이블 헤더
    const termsGridHeader = computed(() => [
        { title: 'No', key: 'index', sortable: true, width:100},
        { title: lang.terms('termsTableHeader.termsType'), key: 'termsType',sortable: true, },
        { title: lang.terms('termsTableHeader.startDate'), key: 'startDate', sortable: true, },
        { title: lang.terms('termsTableHeader.termsPeriod'), key: 'termsPeriod', sortable: true, },
        { title: lang.terms('termsTableHeader.isActive'), key: 'isActive', sortable: true, },
        { title: lang.terms('termsTableHeader.createdDatetime'), key: 'createdDateTime', sortable: true, },
    ])

    // route.path 비교
    const isTermsDetail = computed(() => route.path === routes.termsDetail);
    const isTermsRegister = computed(() => route.path === routes.termsRegister);

    // 초기화, 저장 버튼 활성화 여부
    const isBtnDisabled = computed(() => {
        if (isTermsRegister.value) {
            return !termsRegType.value ||
                   !termsRegContent.value ||
                   termsRegIsActive.value === undefined ||
                   termsRegIsActive.value === null ||
                   !termsRegStartDate.value ||
                   !termsRegPeriod.value
        } else if (isTermsDetail.value) {
            return !termsType.value ||
                   !termsContent.value ||
                   termsIsActive.value === undefined ||
                   termsIsActive.value === null ||
                   !termsStartDate.value ||
                   !termsPeriod.value
        }
    })

    // 미리보기 버튼 활성화 여부
    const isPreviewDisabled = computed(() => {
        if (isTermsRegister.value) {
            return !termsRegContent.value
        } else if (isTermsDetail.value) {
            return !termsContent.value
        }
    })

    // 초기화 다이얼로그
    const dataResetDialog = () => {
        if (isTermsRegister.value) {
            termsRegType.value = null;
            termsRegContent.value = '';
            termsRegIsActive.value = null;
            termsRegStartDate.value = '';
            termsRegPeriod.value = null;
        } else if (isTermsDetail.value) {
            termsType.value = null;
            termsContent.value = '';
            termsIsActive.value = null;
            termsStartDate.value = '';
            termsPeriod.value = null;
        }
    }

    // 약관 동의 API
    const fetchTermsAgree = async (
        termsAgreement: TermsAggrementRequest
    ): Promise<ApiResponse<TermsResultResponse>> => {
        // 1) startDate → Date
        const start = new Date(termsAgreement.startDate)

        // 파라미터 체크
        if (isNaN(start.getTime())) {
            throw new Error('E50002')
        }
        if(termsAgreement.period === undefined || termsAgreement.period === null) {
            throw new Error('E50002')
        }

        // 2) period 기반으로 endDate 계산
        const end = new Date(start)
        end.setFullYear(end.getFullYear() + termsAgreement.period)

        // 3) payload 조립
        const payload = {
            termsId:   termsAgreement.termsId,
            termsType: termsAgreement.termsType,
            startDate: termsAgreement.startDate,
            endDate:   formatDate(end),    // YYYY-MM-DD
        }

        // 4) 호출
        return await fetchWithJwt('/terms/agree', {
            method: 'POST',
            body:   JSON.stringify(payload),
        }) as ApiResponse<TermsResultResponse>
    }

    // 약관 동의 처리
    const handlerTermsAgree = async (termsAggrement: TermsAggrementRequest): Promise<void> => {
        console.log('[handlerTermsAgree] 시작');
        const response = await fetchTermsAgree(termsAggrement);
        if (response.result === 'success') {
            handleSuccessCode('S00009');
        } else if (response.result === 'fail') {
            handleErrorCode(response.message);
        }
        console.log('[handlerTermsAgree] 종료');
    }

    return {
        handlerTermsList,
        handlerTermsDetail,
        handlerTermsUpdate,
        handlerTermsDelete,
        handlerTermsCreate,
        handlerTermsAgree,

        termsId,
        termsType,
        termsContent,
        termsContentEn,
        termsContentZh,
        termsContentHtml,
        termsStartDate,
        termsUpdateDateTime,
        termsPeriod,
        termsIsActive,

        termsRegType,
        termsRegContent,
        termsRegContentEn,
        termsRegContentZh,
        termsRegIsActive,
        termsRegStartDate,
        termsRegPeriod,

        termsArrayList,
        termsRouteId,
        selectedTermsType,
        selectedActive,

        termsGridHeader,
        isBtnDisabled,
        isPreviewDisabled,

        dataResetDialog,

        // 약관 팝업창 관련 변수
        // 팝업용 약관 정보
        popupTerms,
        // 팝업용 약관 내용
        popupTermsContent,
        popupTermsContentEn,
        popupTermsContentZh,
        // 팝업용 약관 html
        popupTermsContentHtml,
        // 팝업용 약관 리스트
        popupTermsList,
        // 최신 약관 조회
        handlerFetchLatestTerms,
        // 사용자가 동의한 약관 목록 조회
        handlerFetchUserAgreedTerms,
    }

}